import { SpaceBetween } from '@amzn/awsui-components-react';
import React from 'react';
import { useApi } from '../../../../store/api.context';
import { useChallenges } from '../../../../store/challenge.context';
import { useUser } from '../../../../store/user.context';
import { Challenge } from '../../../../types/Challenge';
import { LabProvider } from '../../../../types/LabProvider';
import { useFlashbars } from '@/src/store/flashbar.context';
import { getErrorMessage } from '@/src/utils/errors.utils';
import ActiveTestEvent from './ActiveTestEvent';
import ChallengeValidation from './ChallengeValidation';
import CreateTestEvent from './CreateTestEvent';

interface ChallengeTestingDetailProps {
  challenge: Challenge;
}

const ChallengeTestingDetail: React.FC<ChallengeTestingDetailProps> = ({ challenge }) => {
  const {
    testEventName,
    deployTestEvent,
    redeployTestEvent,
    updateOneClickEventTesters,
    terminateTestEvent,
    extendTestEvent,
  } = useChallenges();
  const { user } = useUser();
  const { eventsApi } = useApi();
  const { addErrorFlashbar } = useFlashbars();

  const challengeId = challenge.challengeId;

  const onDeployTestEvent = async (region: string | null) => {
    if (!challengeId) {
      throw new Error('Missing challenge ID');
    }

    try {
      await deployTestEvent(challengeId, LabProvider.AWS_LABS, region, false);
    } catch (e) {
      addErrorFlashbar(getErrorMessage(e));
      throw e;
    }
  };

  const onRedeployTestEvent = () => {
    if (challengeId) {
      void redeployTestEvent(challengeId);
    }
  };

  const onUpdateTesters = (testers: string[]) => {
    if (challengeId && testers) {
      void updateOneClickEventTesters(challengeId, testers);
    }
  };

  const onTerminateTestEvent = () => {
    if (challengeId) {
      void terminateTestEvent(challengeId);
    }
  };

  const onExtendTestEvent = (hours: number) => {
    if (challengeId) {
      void extendTestEvent(challengeId, hours);
    }
  };

  const renderTestEvent = () => {
    if (testEventName) {
      return (
        <ActiveTestEvent
          challenge={challenge}
          testEventName={testEventName}
          user={user}
          eventsApi={eventsApi}
          onRedeployTestEvent={onRedeployTestEvent}
          onUpdateTesters={onUpdateTesters}
          onTerminateTestEvent={onTerminateTestEvent}
          onExtendTestEvent={onExtendTestEvent}
        />
      );
    }

    return <CreateTestEvent challenge={challenge} onDeployTestEvent={onDeployTestEvent} />;
  };

  return (
    <SpaceBetween direction="vertical" size="m">
      {renderTestEvent()}
      {challengeId && <ChallengeValidation challenge={challenge} />}
    </SpaceBetween>
  );
};

export default ChallengeTestingDetail;
